import authenticatedAxios from '@/lib/axios';
import { quickLog } from '@/utils';
import { USE_MOCK_DATA } from '@/constants/config';
import { ApiAvailables, ApiCertification, ApiScenario } from '@/types';
import { cachedGet, DEFAULT_TTL_MS } from './cache';
import {
  mockApiAvailables,
  mockApiCertifications,
  mockApiScenarios,
} from '@/mocks';

/**
 * Get all certifications
 */
export const getCertifications = async (): Promise<ApiCertification[]> => {
  if (USE_MOCK_DATA) {
    quickLog.api('[MOCK DATA] Returning mock certifications');
    return mockApiCertifications;
  }

  const endpoint = 'app/certifications';
  const response = await authenticatedAxios.get<ApiCertification[]>(endpoint);
  return response.data;
};

/**
 * Get a specific certification by ID
 */
export const getCertificationById = async (id: number): Promise<ApiCertification> => {
  if (USE_MOCK_DATA) {
    quickLog.api('[MOCK DATA] Returning mock certification by ID:', id);
    const certification = mockApiCertifications.find((cert) => cert.id === id);
    if (!certification) throw new Error(`Certification with ID ${id} not found`);
    return certification;
  }

  const response = await authenticatedAxios.get<ApiCertification>(`app/certifications/${id}`);
  return response.data;
};

/**
 * Get all scenarios (practice roleplays) for a certification
 */
export const getScenariosByCertification = async (
  certificationId: number,
): Promise<ApiScenario[]> => {
  if (USE_MOCK_DATA) {
    quickLog.api('[MOCK DATA] Returning mock scenarios for certification ID:', certificationId);
    return mockApiScenarios.filter((sc) => sc.certification_id === certificationId);
  }

  const endpoint = `app/scenarios/certification/${certificationId}`;
  const response = await authenticatedAxios.get<ApiScenario[]>(endpoint);
  return response.data;
};

/**
 * Get user's availables (certifications and simulations they can enroll in)
 */
export const getUserAvailables = async (userId: string): Promise<ApiAvailables[]> => {
  if (USE_MOCK_DATA) {
    quickLog.api('[MOCK DATA] Returning mock user availables for user ID:', userId);
    return mockApiAvailables;
  }

  const endpoint = `app/certifications/user/${userId}/available`;
  return cachedGet(
    `certifications.availables:${userId}`,
    async () => {
      const response = await authenticatedAxios.get<ApiAvailables[]>(endpoint);
      return response.data;
    },
    DEFAULT_TTL_MS,
  );
};


